import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Redirect, useHistory } from "react-router-dom";
import MainContext from '../../Context/MainContext/MainContext';

import Queue from "../Queue/Queue";
import InboundFiles from "../InboundFIles/InboundFiles";
import MyAccounts from "../Report/MyAccounts";

import LayerFunctional from "../../LayerFunctional";
import DashboardUtility from "../../Utility/Crud/Dashboard";
import DashboardWidget from "../../Utility/Crud/DashboardWidget";

const blockComponentRegistry = {
    'WidgetDashboardQueue': Queue,
    'WidgetDashboardOOB': Queue,
    'WidgetDashboardMultiQueue': Queue,
    'WidgetDashboardAccounts': MyAccounts,
    'WidgetDashboardPoints': Queue,
    'WidgetDashboardPresidentsClub': Queue,
    'WidgetDashboardActivities': Queue,
    'WidgetDashboardInboundFiles': InboundFiles,
};

/**
 * Layer for `/dashboard/:dashboardId` and `/dashboard/:dashboardId/:widgetId`.
 * With no widget identifier in the path, redirects to the first standard queue widget.
 * With a widget identifier, resolves the dashboard block component for that widget.
 *
 * @returns {React.ReactElement}
 */
export default function Dashboard(props) {
    const mainContext = useContext(MainContext);
    const history = useHistory();
    const [dashboardWidgets, setDashboardWidgets] = useState([]);
    /**
     * Holds the latest configuration reported by a dashboard widget, plus which dashboard widget row
     * submitted it, so we can ignore stale values after the route switches to another widget.
     */
    const [widgetConfigurationEnvelope, setWidgetConfigurationEnvelope] = useState(null);

    const dashboardId = props.match.params.dashboardId;
    const widgetIdFromRoute = props.match.params.widgetId;

    /**
     * The default dashboard page is the first standard queue widget. Summary
     * widgets such as OOB can be ordered first without becoming the page that
     * the dashboard initially attempts to load.
     */
    const defaultDashboardWidget = useMemo(() => {
        return dashboardWidgets.find((dashboardWidget) => {
            return mainContext.widgetsIndexed[dashboardWidget.widgetId]
                ?.widgetComponentTermId === 219; // WidgetDashboardQueue
        }) ?? dashboardWidgets[0] ?? null;
    }, [dashboardWidgets, mainContext.widgetsIndexed]);

    /**
     * Get the active widget from the route
     */
    const activeWidget = useMemo(() => {
        if (dashboardWidgets.length === 0) {
            return null;
        }
        const widgetMatchingRoute = dashboardWidgets.find(
            (widget) => String(widget.id) === String(widgetIdFromRoute)
        );

        return widgetMatchingRoute ?? defaultDashboardWidget;
    }, [dashboardWidgets, widgetIdFromRoute, defaultDashboardWidget]);

    /**
     * Configuration object for the block component (Queue, MyAccounts, etc.) only when it belongs to the
     * active dashboard widget. Otherwise null until the active widget reports its configuration.
     */
    const widgetConfiguration = useMemo(() => {
        if (widgetConfigurationEnvelope === null) {
            return null;
        }
        if (activeWidget === null || activeWidget === undefined) {
            return null;
        }
        if (
            String(widgetConfigurationEnvelope.dashboardWidgetIdentifier) !==
            String(activeWidget.id)
        ) {
            return null;
        }
        return widgetConfigurationEnvelope.configurationFromWidget;
    }, [widgetConfigurationEnvelope, activeWidget]);

    /**
     * Get the widgets for the dashboard
     */
    useEffect(() => {
        if (!dashboardId) {
            return;
        }

        const dashboardWidgetRows = DashboardUtility.sort(DashboardUtility.getDashboardWidgetRows(dashboardId, mainContext.dashboardWidgetsIndexed));

        setDashboardWidgets(DashboardWidget.sort(dashboardWidgetRows));
    }, [dashboardId]);

    /**
     * When the route names a widget that is not part of this dashboard, normalize the location to the first widget.
     */
    useEffect(() => {
        if (dashboardWidgets.length === 0 || !widgetIdFromRoute) {
            return;
        }

        const widgetMatchingRoute = dashboardWidgets.find(
            (widget) => String(widget.id) === String(widgetIdFromRoute)
        );

        if (!widgetMatchingRoute) {
            history.replace(`/dashboard/${dashboardId}/${defaultDashboardWidget.id}`);
        }
    }, [dashboardWidgets, widgetIdFromRoute, dashboardId, history, defaultDashboardWidget]);

    /**
     * @param {object} selectedWidget Widget record from {@link DashboardUtility.getWidgetsForDashboard}.
     */
    const handleActiveWidgetChange = useCallback(
        (selectedWidget) => {
            if (
                selectedWidget &&
                String(selectedWidget.id) !== String(widgetIdFromRoute)
            ) {
                history.push(`/dashboard/${dashboardId}/${selectedWidget.id}`);
            }
        },
        [dashboardId, widgetIdFromRoute, history]
    );

    const updateActiveWidgetConfiguration = useCallback(
        (configurationFromWidget) => {
            if (activeWidget === null || activeWidget === undefined) {
                return;
            }
            setWidgetConfigurationEnvelope({
                dashboardWidgetIdentifier: activeWidget.id,
                configurationFromWidget: configurationFromWidget ?? null,
            });
        },
        [activeWidget]
    );

    const renderContent = () => {
        if (!dashboardWidgets.length) {
            return null;
        }

        if (!widgetIdFromRoute) {
            return <Redirect to={`/dashboard/${dashboardId}/${defaultDashboardWidget.id}`} />;
        }

        const activeWidgetConfiguration = mainContext.widgetsIndexed[activeWidget.widgetId];
        if (!activeWidgetConfiguration) {
            return null;
        }

        const widgetComponentTerm = mainContext.termsIndexed[activeWidgetConfiguration.widgetComponentTermId];
        if (!widgetComponentTerm) {
            return null;
        }

        const ComponentToRender = blockComponentRegistry[widgetComponentTerm.name];

        if (!ComponentToRender) {
            return null;
        }

        return (
            <ComponentToRender
                key={String(activeWidget.id)}
                {...props}
                dashboardWidgets={dashboardWidgets}
                activeWidget={activeWidget}
                onActiveWidgetChange={handleActiveWidgetChange}
                updateActiveWidgetConfiguration={updateActiveWidgetConfiguration}
                widgetConfiguration={widgetConfiguration}
            />
        );
    };

    return (
        <LayerFunctional
            passthrough={true}
            renderContent={() => renderContent()}
            getMaxWidth={() => 1900}
        />
    );
}
